import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, LocateFixed, MapPinned } from "lucide-react";
import type { Stop } from "../lib/types";

declare global {
  interface Window {
    google?: any;
    __viaVivaGooglePromise?: Promise<void>;
    __viaVivaGoogleReady?: () => void;
  }
}

async function loadGoogle(key: string) {
  if (!window.google?.maps?.importLibrary && !window.__viaVivaGooglePromise) {
    window.__viaVivaGooglePromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      window.__viaVivaGoogleReady = () => {
        delete window.__viaVivaGoogleReady;
        resolve();
      };
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=marker&v=weekly&loading=async&callback=__viaVivaGoogleReady`;
      script.async = true;
      script.onerror = () => {
        delete window.__viaVivaGoogleReady;
        window.__viaVivaGooglePromise = undefined;
        reject(new Error("Google Maps no respondió"));
      };
      document.head.appendChild(script);
    });
  }
  if (window.__viaVivaGooglePromise) await window.__viaVivaGooglePromise;
  const importLibrary = window.google?.maps?.importLibrary;
  if (typeof importLibrary !== "function") throw new Error("Google Maps no terminó de cargar");
  await Promise.all([importLibrary("core"), importLibrary("maps"), importLibrary("marker")]);
  if (typeof window.google?.maps?.LatLngBounds !== "function" || typeof window.google?.maps?.Map !== "function") {
    throw new Error("Google Maps no terminó de preparar el mapa");
  }
}

interface Props { stops: Stop[]; selectedId: string | null; onSelect: (id: string) => void; googleKey: string | null }
export function TripMap({ stops, selectedId, onSelect, googleKey }: Props) {
  const mapNode = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);
  const points = useMemo(() => stops.filter((stop) => stop.latitude != null && stop.longitude != null && stop.state !== "skipped" && (stop.kind !== "suggested" || stop.accepted)), [stops]);
  const selected = stops.find((stop) => stop.id === selectedId) ?? points[0];

  useEffect(() => {
    if (!googleKey || !mapNode.current || !points.length) return;
    let disposed = false;
    loadGoogle(googleKey).then(() => {
      if (disposed || !mapNode.current) return;
      const google = window.google!;
      const bounds = new google.maps.LatLngBounds();
      map.current = new google.maps.Map(mapNode.current, { mapTypeControl: false, streetViewControl: false, fullscreenControl: false, gestureHandling: "greedy", zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER } });
      const routePath = points.map((stop) => ({ lat: stop.latitude!, lng: stop.longitude! }));
      new google.maps.Polyline({ path: routePath, geodesic: true, strokeColor: "#da6c43", strokeOpacity: 0.9, strokeWeight: 4, map: map.current });
      const info = new google.maps.InfoWindow();
      points.forEach((stop, index) => {
        const position = routePath[index]; bounds.extend(position);
        const marker = new google.maps.Marker({ position, map: map.current, label: String(index + 1), title: stop.name });
        marker.addListener("click", () => { onSelect(stop.id); info.setContent(`<strong>${stop.name.replace(/[<>&]/g, "")}</strong>`); info.open({ map: map.current, anchor: marker }); });
      });
      map.current.fitBounds(bounds, 48);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "Mapa no disponible"));
    return () => { disposed = true; };
  }, [googleKey, points, onSelect]);

  function centerSelected() {
    if (map.current && selected?.latitude != null && selected.longitude != null) { map.current.panTo({ lat: selected.latitude, lng: selected.longitude }); map.current.setZoom(14); }
  }
  const navigation = selected?.latitude != null && selected.longitude != null ? `https://www.google.com/maps/dir/?api=1&destination=${selected.latitude},${selected.longitude}${selected.placeId ? `&destination_place_id=${encodeURIComponent(selected.placeId)}` : ""}` : null;

  return <section className="map-shell" aria-label="Mapa del itinerario">
    {googleKey && !error ? <div ref={mapNode} className="google-map" /> : <div className="map-fallback">
      <MapPinned size={34} aria-hidden="true" />
      <strong>{error ?? "Mapa listo para conectar"}</strong>
      <span>{googleKey ? "Reintentaremos al actualizar." : "Agrega las llaves restringidas de Google para ver tráfico y rutas en vivo."}</span>
      <div className="fallback-route" aria-label="Secuencia de paradas">
        {points.slice(0, 6).map((stop, index) => <button key={stop.id} onClick={() => onSelect(stop.id)} className={stop.id === selectedId ? "is-active" : ""}><i>{index + 1}</i>{stop.publicLabel}</button>)}
      </div>
    </div>}
    <div className="map-actions">
      <button className="icon-button" onClick={centerSelected} aria-label="Centrar parada"><LocateFixed size={18} /></button>
      {navigation && <a className="map-nav" href={navigation} target="_blank" rel="noreferrer"><ExternalLink size={16} /> Navegar</a>}
    </div>
  </section>;
}
